import React, { useEffect, useState } from "react";
import { m } from "framer-motion";
// @mui
import { alpha, styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Avatar from "@mui/material/Avatar";
import Divider from "@mui/material/Divider";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import { varHover } from "@/components/animate";
import { avatars } from "@/utils/profileMetaData";
import AltAvatar from "@/components/alt-avatar";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import CustomPopover, { usePopover } from "@/components/custom-popover";
import { Typography } from "@mui/material";
import { Iconify } from "@/components/iconify/iconify";
import { useGetDistrictById } from "../../hooks/useGetDistrictById";
import { logout } from "@/utils/logout";
import { useProductFeatures } from "../../hooks/useProductFeatures";
import { useLocales } from "@/locales";

// ----------------------------------------------------------------------

const MenuLink = styled("a")(({ theme }) => ({
  color: theme.palette.text.primary,
  textDecoration: "none",
  fontWeight: 400,
  svg: {
    position: "relative",
    top: "3px",
    marginLeft: "4px",
  },
}));

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const currentUser = useCurrentUser();

  const { data: district } = useGetDistrictById(currentUser.districtId);
  // Add state for userAvatar
  const [userAvatar, setUserAvatar] = useState(null);
  const { onChangeLang } = useLocales();
  const { msPlans, wblPlans } = useProductFeatures();
  useEffect(() => {
    const avatarObj = avatars.find(
      (avatar) => avatar.id === currentUser.profileChoices?.avatar,
    );
    setUserAvatar(avatarObj ? avatarObj.src : null);
  }, [currentUser.profileChoices]);

  useEffect(() => {
    const districtNomenclature = district?.nomenclature;
    if (districtNomenclature !== undefined) {
      const lang = `en_${districtNomenclature}`;
      onChangeLang(lang);
    }
  }, [district?.nomenclature]);
  const popover = usePopover();
  const handleLogout = () => {
    popover.onClose();
    logout(district?.loginMethod);
  };

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        onClick={popover.onOpen}
        sx={{
          width: 40,
          height: 40,
          background: (theme) => alpha(theme.palette.grey[0], 0.08),
          ...(popover.open && {
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
          }),
        }}
      >
        {userAvatar ? (
          <Avatar
            sx={{
              width: 36,
              height: 36,
              fontWeight: "bold",
              margin: "0 auto",
            }}
            src={userAvatar}
          ></Avatar>
        ) : (
          <Stack
            sx={{
              margin: "0 auto",
            }}
          >
            <AltAvatar
              initials={`${currentUser.firstName?.[0]}${currentUser.lastName?.[0]}`.toUpperCase()}
              sx={{ margin: "0 auto" }}
            />
          </Stack>
        )}
      </IconButton>

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        sx={{ width: 200, p: 0 }}
      >
        <Box sx={{ p: 2, pb: 1.5 }}>
          <Typography variant="subtitle2" noWrap>
            {currentUser.firstName} {currentUser.lastName}
          </Typography>

          <Typography variant="body2" sx={{ color: "text.secondary" }} noWrap>
            {currentUser.username}
          </Typography>
        </Box>
        <Divider sx={{ borderStyle: "dashed" }} />
        {msPlans.length === 0 && wblPlans.length === 0 ? (
          <>
            <MenuItem
              sx={{
                m: 1,
                fontWeight: "fontWeightBold",
                textDecoration: "none",
              }}
            >
              <MenuLink
                href="https://help.hatsandladders.com/"
                target="_blank"
                rel="noreferrer"
              >
                Help Center
                <Iconify icon="eva:external-link-fill" width={16} />
              </MenuLink>
            </MenuItem>
            <MenuItem
              sx={{
                m: 1,
                fontWeight: "fontWeightBold",
                textDecoration: "none",
              }}
            >
              <MenuLink
                href="https://hatsandladders.com/contact-support"
                target="_blank"
                rel="noreferrer"
              >
                Submit a Ticket
                <Iconify icon="eva:external-link-fill" width={16} />
              </MenuLink>
            </MenuItem>
          </>
        ) : null}
        {/* <Stack sx={{ p: 1 }}>
          {OPTIONS.map((option) => (
            <MenuItem
              key={option.label}
              onClick={() => handleClickItem(option.linkTo)}
            >
              {option.label}
            </MenuItem>
          ))}
        </Stack> */}
        <Divider sx={{ borderStyle: "dashed" }} />
        <MenuItem
          onClick={handleLogout}
          sx={{ m: 1, fontWeight: "fontWeightBold", color: "error.main" }}
        >
          Logout
        </MenuItem>
      </CustomPopover>
    </>
  );
}
